import { Component, OnInit } from '@angular/core';

import { AppService } from '../../services/app.service';
import { DiplomaSupplementService } from '../../services/diploma-supplement.service';
import { ActivatedRoute } from '@angular/router';
import { DiplomaSupplementFileType, IDiplomaSupplement, IDiplomaSupplementCourse, IDiplomaSupplementEditModel } from '../../models/DiplomaSupplement';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { Utils } from '../../core/Utils';
import { finalize, tap } from 'rxjs/operators';
import { IClassifier } from '../../models/Classifier';
import { ClassifierService } from '../../services/classifier.service';
import { IValuePair, Language } from './common';

interface ICourseGradeRow extends IDiplomaSupplementCourse {
    part: string;
}

@Component({
    selector: 'app-diploma-supplement-form',
    templateUrl: './form.component.html',
    styleUrls: ['./diploma-supplement.component.scss']
})
export class DiplomaSupplementFormComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: DiplomaSupplementService,
        private classifiers: ClassifierService,
        private route: ActivatedRoute,
        private translateService: TranslateService
    ) { }

    item: IDiplomaSupplement;

    readonly editModel: IDiplomaSupplementEditModel = <any>{};

    version?: IDiplomaSupplement;
    changes: string[] = [];
    signers: IClassifier[] = [];
    isEditing: boolean;
    isRefreshing: boolean;
    isSaving: boolean;
    isSubmitting: boolean;

    blocks: {
        index: string,
        title: string
    }[] = [];

    valuePairs: {
        index: string,
        pairs: IValuePair[]
    }[] = [];

    //// course grades by part
    //courses: {
    //    part: string,
    //    items: IDiplomaSupplementCourse[]
    //}[] = [];

    // course grades flat
    courseGrades: ICourseGradeRow[] = [];

    readonly translate = (key: string, language: Language) => this.text(key, language);

    get programName(): string {
        return this.app.currentLanguage == 'en' ? this.item.ProgramNameEn : this.item.ProgramName;
    }

    get isRegistered(): boolean {
        return this.item.Status == 'Registered';
    }

    get canEdit(): boolean {
        return this.app.currentUser.hasRight('DIPLOMA_SUPPLEMENT.EDIT');
    }

    private id: number;
    private personId: string;
    private programCode: string;

    private readonly translations: { lv: any, en: any } = {
        lv: {}, en: {}
    };

    ngOnInit() {
        this.route.params.subscribe(() => {
            const id = this.route.snapshot.params.id;

            if (id == 'form') {
                this.personId = Utils.findParam(this.route.snapshot.queryParams, 'PersonId');
                this.programCode = Utils.findParam(this.route.snapshot.queryParams, 'ProgramCode');

                this.app.addLoading(this.service.find(this.personId, this.programCode)).subscribe(data => {
                    if (data) {
                        this.app.navigate(['/diploma-supplements', data.Id]);
                    } else {
                        this.init();
                    }
                });
            } else {
                this.id = +id;
                this.init();
            }
        });
    }

    text(key: string, language: Language): string {
        return this.translateService.getParsedResult(this.translations[language], key);
    }

    lv(key: string): string {
        return this.text(key, 'lv');
    }

    en(key: string): string {
        return this.text(key, 'en');
    }

    save(isFinal: boolean = false) {
        if (isFinal) {
            const errors: string[] = [];

            if (!this.editModel.DiplomaSeries) {
                errors.push(this.app.translate('diplomaSupplement_diplomaSeriesRequired'));
            }

            if (!this.editModel.DiplomaNumber) {
                errors.push(this.app.translate('diplomaSupplement_diplomaNumberRequired'));
            }

            if (!this.editModel.SignerName) {
                errors.push(this.app.translate('diplomaSupplement_signerNameRequired'));
            }

            if (!this.editModel.Signed) {
                errors.push(this.app.translate('diplomaSupplement_signedRequired'));
            }

            if (!this.editModel.SignerPosition) {
                errors.push(this.app.translate('diplomaSupplement_signerPositionRequired'));
            }

            if (!this.editModel.SignerPositionEn) {
                errors.push(this.app.translate('diplomaSupplement_signerPositionEnRequired'));
            }

            if (errors.length) {
                this.app.alert.warning(errors.join('<br>'), this.app.translate('invalidFormWarning'));
                return;
            }
        }

        if (isFinal) this.isSubmitting = true;
        else this.isSaving = true;

        const req = this.item.Id
            ? this.service.update(this.item.Id, this.editModel, isFinal)
            : this.service.create({
                ...this.editModel,
                PersonId: this.item.PersonId,
                ProgramCode: this.item.ProgramCode
            }).pipe(tap(newId => {
                this.id = newId;
            }));

        return this.app.addLoading(req).pipe(finalize(() => {
            this.isSaving = false;
            this.isSubmitting = false;
        })).subscribe(() => {
            this.app.notify(isFinal ? 'diplomaSupplement_registered' : 'diplomaSupplement_saved');
            this.fetch(true);
        });
    }

    refresh() {
        this.isRefreshing = true;

        this.app.addLoading(this.service.getData(this.item.PersonId, this.item.ProgramCode)).pipe(finalize(() => {
            this.isRefreshing = false;
        })).subscribe(data => {
            this.reset();

            this.version = data;

            if (data.DiplomaSeries) {
                this.editModel.DiplomaSeries = data.DiplomaSeries;

                if (data.DiplomaSeries != this.item.DiplomaSeries) {
                    this.changes.push(this.app.translate('diplomaSupplement_lblDiplomaSeries'));
                }
            }

            if (data.DiplomaNumber) {
                this.editModel.DiplomaNumber = data.DiplomaNumber;

                if (data.DiplomaNumber != this.item.DiplomaNumber) {
                    this.changes.push(this.app.translate('diplomaSupplement_lblDiplomaNumber'));
                }
            }

            this.isEditing = this.canEdit;
            this.createBlocks();
        });
    }

    getBlock(index: string) {
        return this.blocks.find(t => t.index == index);
    }

    getValues(index: string) {
        return this.valuePairs.find(t => t.index == index)?.pairs || [];
    }

    download(type: DiplomaSupplementFileType) {
        this.app.addLoading(this.service.download(this.id, type)).subscribe();
    }

    private fetch(scrollToTop: boolean = false) {
        const setup = () => {
            this.reset();

            this.isEditing = this.item.Status == 'Draft' && this.canEdit;

            this.editModel.AdditionalNotesFree = this.item.AdditionalNotesFree;
            this.editModel.AdditionalNotesFreeEn = this.item.AdditionalNotesFreeEn;
            this.editModel.DiplomaSeries = this.item.DiplomaSeries;
            this.editModel.DiplomaNumber = this.item.DiplomaNumber;
            this.editModel.Signed = Utils.getInputDate(this.item.Signed);
            this.editModel.SignerName = this.item.SignerName;
            this.editModel.SignerPosition = this.item.SignerPosition;
            this.editModel.SignerPositionEn = this.item.SignerPositionEn;

            this.createBlocks();

            if (scrollToTop) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };

        if (this.id) {
            this.app.addLoading(this.service.getById(this.id)).subscribe(data => {
                this.item = data;
                setup();
            });
        } else {
            this.app.addLoading(this.service.getData(this.personId, this.programCode)).subscribe(data => {
                this.item = {
                    ...data,
                    Status: 'Draft'
                };

                setup();
            });
        }
    }

    private init() {
        //const textCodes = [
        //    messageCodes.awardingInsitution,
        //    messageCodes.adminInsitution,
        //    messageCodes.additionalNotesSource
        //];

        this.app.addLoading(forkJoin([
            this.translateService.getTranslation('lv'),
            this.translateService.getTranslation('en'),
            this.classifiers.get('DiplomaSupplementSigner', true)
            //this.messages.getText(textCodes, 'lv', true),
            //this.messages.getText(textCodes, 'en', true)
        ])).subscribe(([lv, en/*, messageLv, messageEn*/, cls]) => {
            this.translations.lv = lv;
            this.translations.en = en;

            this.signers = cls.filter(t => t.Type == 'DiplomaSupplementSigner');
            //this.editModel.AwardingInstitution = messageLv.find(t => t.Code == messageCodes.awardingInsitution)?.Text;
            //this.editModel.AwardingInstitutionEn = messageEn.find(t => t.Code == messageCodes.awardingInsitution)?.Text;

            //this.editModel.AdminInstitution = messageLv.find(t => t.Code == messageCodes.adminInsitution)?.Text;
            //this.editModel.AdminInstitutionEn = messageEn.find(t => t.Code == messageCodes.adminInsitution)?.Text;

            //this.editModel.AdditionalNotesSource = messageLv.find(t => t.Code == messageCodes.additionalNotesSource)?.Text;
            //this.editModel.AdditionalNotesSourceEn = messageEn.find(t => t.Code == messageCodes.additionalNotesSource)?.Text;

            this.fetch();
        });
    }

    private reset() {
        this.blocks = [];
        this.valuePairs = [];
        //this.courses = [];
        this.courseGrades = [];
        this.changes = [];

        this.version = undefined;
    }

    private getDateString(date: string | Date, language: Language): string {
        if (!date) return '';

        const parts = Utils.parseDateTime(date);

        return language == 'en'
            ? `${parts.dateString}/${parts.monthString}/${parts.yearString}`
            : `${parts.dateString}.${parts.monthString}.${parts.yearString}`;
    }

    private getYearsEctsString(data: IDiplomaSupplement, language: Language): string {
        const years = data.ProgramDurationYears;
        const semesters = years * 2;
        const ects = data.ProgramDurationEcts;
        const creditPoints = ects / 1.5;

        return this.text('diplomaSupplement_studyDurationYearsEcts', language)
            .replace('{{years}}', years.toString())
            .replace('{{semesters}}', semesters.toString())
            .replace('{{cp}}', creditPoints.toString())
            .replace('{{ects}}', ects.toString());
    }

    private createBlocks() {
        this.blocks.push(...[
            {
                index: '1',
                title: 'diplomaSupplement_qualificationHolderInfo'
            },
            {
                index: '2',
                title: 'diplomaSupplement_qualificationInfo'
            },
            {
                index: '3',
                title: 'diplomaSupplement_qualificationLevelInfo'
            },
            {
                index: '4',
                title: 'diplomaSupplement_contentAndResultInfo'
            },
            {
                index: '5',
                title: 'diplomaSupplement_qualificationFunctionInfo'
            },
            {
                index: '6',
                title: 'diplomaSupplement_additionalInfo'
            },
            {
                index: '7',
                title: 'diplomaSupplement_certificationInfo'
            }
        ]);

        const createValuePair = (index: string, label: string, property: (data: IDiplomaSupplement, language: Language) => string): IValuePair => {
            return {
                index,
                label: `diplomaSupplement_${label}`,
                lv: {
                    value: property(this.item, 'lv'),
                    next: this.version ? property(this.version, 'lv') : undefined
                },
                en: {
                    value: property(this.item, 'en'),
                    next: this.version ? property(this.version, 'en') : undefined
                }
            };
        };

        this.valuePairs.push({
            index: '1',
            pairs: [
                createValuePair('1.1', 'lblFirstName', (data, lang) => data.FirstName),
                createValuePair('1.2', 'lblLastName', (data, lang) => data.LastName),
                createValuePair('1.3', 'lblBirthdate', (data, lang) => this.getDateString(data.Birthdate, lang)),
                createValuePair('1.4', 'lblPersonCode', (data, lang) => data.PersonCode)
            ]
        });

        this.valuePairs.push({
            index: '2',
            pairs: [
                createValuePair('2.1', 'lblQualificationName', (data, lang) => lang == 'en' ? data.QualificationNameEn : data.QualificationName),
                createValuePair('2.2', 'lblMainStudyField', (data, lang) => lang == 'en' ? data.MainStudyFieldEn : data.MainStudyField),
                createValuePair('2.3', 'lblQualificationAwardingInstitution', (data, lang) => lang == 'en' ? data.AwardingInstitutionEn : data.AwardingInstitution),
                createValuePair('2.4', 'lblQualificationAdminInstitution', (data, lang) => lang == 'en' ? data.AdminInstitutionEn : data.AdminInstitution),
                createValuePair('2.5', 'lblStudyLanguage', (data, lang) => lang == 'en' ? data.StudyLanguageEn : data.StudyLanguage),
            ]
        });

        this.valuePairs.push({
            index: '3',
            pairs: [
                createValuePair('3.1', 'lblQualificationLevel', (data, lang) => lang == 'en' ? data.QualificationLevelEn : data.QualificationLevel),
                createValuePair('3.2', 'lblStudyDurationYearsEcts', (data, lang) => this.getYearsEctsString(data, lang)),
                createValuePair('3.3', 'lblAdminissionRequirements', (data, lang) => lang == 'en' ? data.AdmissionRequirementsEn : data.AdmissionRequirements)
            ]
        });

        this.valuePairs.push({
            index: '4',
            pairs: [
                createValuePair('4.1', 'lblStudyMode', (data, lang) => lang == 'en' ? data.StudyModeEn : data.StudyMode),
                createValuePair('4.1', 'lblProgramRequirements', (data, lang) => lang == 'en' ? data.ProgramGoalsResultsEn : data.ProgramGoalsResults)
            ]
        });

        this.valuePairs.push({
            index: '4.5',
            pairs: [
                createValuePair('4.5', 'lblQualificationClass', (data, lang) => lang == 'en' ? data.QualificationClassEn : data.QualificationClass)
            ]
        });

        this.valuePairs.push({
            index: '5',
            pairs: [
                createValuePair('5.1', 'lblFurtherStudyOptions', (data, lang) => lang == 'en' ? data.FurtherStudyOptionsEn : data.FurtherStudyOptions),
                createValuePair('5.2', 'lblProfessionalStatus', (data, lang) => lang == 'en' ? data.ProfessionalStatusEn : data.ProfessionalStatus)
            ]
        });

        const additionalInfoFree = createValuePair('6.1.1', 'lblAdditionalInfoFree', (data, lang) => lang == 'en' ? data.AdditionalNotesFreeEn : data.AdditionalNotesFree);

        if (this.isEditing) {
            additionalInfoFree.lv.edit = {
                type: 'note',
                value: this.item.AdditionalNotesFree,
                onChange: (val: string) => {
                    this.editModel.AdditionalNotesFree = val;
                }
            };
            additionalInfoFree.en.edit = {
                type: 'note',
                value: this.item.AdditionalNotesFreeEn,
                onChange: (val: string) => {
                    this.editModel.AdditionalNotesFreeEn = val;
                }
            };
        }

        this.valuePairs.push({
            index: '6',
            pairs: [
                createValuePair('6.1', 'lblAdditionalInfo', (data, lang) => lang == 'en' ? data.AdditionalNotesEn : data.AdditionalNotes),
                additionalInfoFree,
                createValuePair('6.2', 'lblAdditionalInfoSource', (data, lang) => lang == 'en' ? data.AdditionalNotesSourceEn : data.AdditionalNotesSource),
            ]
        });

        const signed = createValuePair('7.1', 'lblSigningDate', (data, lang) => this.getDateString(this.editModel.Signed, lang));

        if (this.isEditing) {
            signed.lv.edit = {
                type: this.isEditing ? 'date' : 'text',
                required: true,
                max: new Date(),
                value: this.isEditing ? this.editModel.Signed : this.getDateString(this.editModel.Signed, 'lv'),
                onChange: val => {
                    this.editModel.Signed = val;
                    signed.en.edit.value = this.getDateString(this.editModel.Signed, 'en');
                }
            };
            signed.en.edit = {
                type: 'text',
                required: true,
                disabled: true,
                value: this.getDateString(this.editModel.Signed, 'en')
            };
        }

        const signerPosition = createValuePair('7.3', 'lblSignerPosition', (data, lang) => lang == 'en' ? data.SignerPosition : data.SignerPositionEn);

        if (this.isEditing) {
            signerPosition.lv.edit = {
                type: 'text',
                required: true,
                disabled: true,
                value: this.item.SignerPosition
            };
            signerPosition.en.edit = {
                type: 'text',
                required: true,
                disabled: true,
                value: this.item.SignerPositionEn
            };
        }

        const signer = createValuePair('7.2', 'lblSignatory', (data, lang) => data.SignerName);

        if (this.isEditing) {
            const signerOptions = this.signers.map(t => t.Value);

            if (this.item.SignerName && !signerOptions.some(t => t == this.item.SignerName)) {
                // ensure original value is still in the list
                signerOptions.unshift(this.item.SignerName);
            }

            signer.lv.edit = {
                type: this.isEditing ? 'choice' : 'text',
                required: true,
                choices: signerOptions,
                value: this.item.SignerName,
                onChange: (val: string) => {
                    if (val) {
                        const opt = this.signers.find(t => t.Value == val);

                        if (opt) {
                            const payload = JSON.parse(opt.Payload || '{}');

                            this.editModel.SignerName = opt.Value;
                            this.editModel.SignerPosition = payload.Position;
                            this.editModel.SignerPositionEn = payload.PositionEn;
                        } else {
                            // fallback to original value
                            this.editModel.SignerName = this.item.SignerName;
                            this.editModel.SignerPosition = this.item.SignerPosition;
                            this.editModel.SignerPositionEn = this.item.SignerPositionEn;
                        }
                    } else {
                        // allow empty values while in draft
                        this.editModel.SignerName = undefined;
                        this.editModel.SignerPosition = undefined;
                        this.editModel.SignerPositionEn = undefined;
                    }

                    signer.en.edit.value = this.editModel.SignerName;

                    signerPosition.lv.edit.value = this.editModel.SignerPosition;
                    signerPosition.en.edit.value = this.editModel.SignerPositionEn;
                }
            };
            signer.en.edit = {
                type: 'text',
                required: true,
                disabled: true,
                value: this.item.SignerName
            };
        }

        this.valuePairs.push({
            index: '7',
            pairs: [
                signed,
                signerPosition,
                signer
            ]
        });

        this.item.Courses.forEach(n => {
            //let part = this.courses.find(t => t.part == n.Part);

            //if (!part) {
            //    part = { part: n.Part, items: [] };
            //    this.courses.push(part);
            //}

            //part.items.push(n);

            this.courseGrades.push({
                ...n,
                part: n.Part
            });
        });

        if (this.version) {
            this.valuePairs.forEach(g => {
                g.pairs.forEach(t => {
                    const labels: string[] = [];

                    if (t.lv.next != t.lv.value) labels.push(this.translate(t.label, 'lv'));
                    if (t.en.next != t.en.value) labels.push(this.translate(t.label, 'en'));

                    if (labels.length) {
                        this.changes.push(`${t.index} ${labels.join(' / ')}`);
                    }
                });
            });
        }
    }
}
